/**
 * NegotiationDetail — the negotiation page's body (plan §10.1, §10.3, §11 tab 2,
 * §13.4 WP-34 frontend scope: "a negotiation view with the four date
 * controls (received, agreed, fully executed, email received; each posts a
 * PUT on change), status, a recruitment difficulty control 1 to 5, and two
 * labelled empty panel slots named Versions and Calculator for the later
 * WPs"). recruitment_difficulty lives on the study, not the negotiation
 * (plan §5.3) — this view's control edits it through PUT /studies/:id, kept
 * in step with the study passed in.
 *
 * WP-103, decision (290), 2026-09-29: this is the body of the former
 * NegotiationModal, laid out on a page — a summary card, then the Versions
 * panel and the Calculator, each full width. Rendered by NegotiationPage only.
 */
import { useState } from 'react';
import { fieldCls, FilterSelect, useToast } from '../../../ui';
import { CalculatorPanel } from '../../components/calculator/CalculatorPanel';
import { updateNegotiation, updateStudy } from '../../api/client';
import { VersionsPanel } from '../../components/versionsPanel';
import type { Negotiation, NegotiationPatch, NegotiationStatus, Study } from '../../api/client';

const STATUS_OPTIONS: Array<{ value: NegotiationStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'agreed', label: 'Agreed' },
  { value: 'executed', label: 'Executed' },
  { value: 'closed', label: 'Closed' },
];

const RECRUITMENT_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: '1', label: '1 — easiest' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5 — hardest' },
];

type DateFieldKey = 'receivedAt' | 'agreedAt' | 'feAt' | 'emailReceivedAt';

const DATE_FIELDS: Array<{ key: DateFieldKey; label: string; pick: (n: Negotiation) => string | null }> = [
  { key: 'receivedAt', label: 'Received', pick: (n) => n.received_at },
  { key: 'agreedAt', label: 'Agreed', pick: (n) => n.agreed_at },
  { key: 'feAt', label: 'Fully executed', pick: (n) => n.fe_at },
  { key: 'emailReceivedAt', label: 'Email received (§10.3: the covering email date)', pick: (n) => n.email_received_at },
];

export const KIND_LABELS: Record<Negotiation['kind'], string> = {
  budget: 'Budget negotiation',
  contract: 'Contract negotiation',
  amendment_budget: 'Budget amendment',
  amendment_contract: 'Contract amendment',
};

/** The negotiation's name in the breadcrumb: kind, then its label when it has one. */
export function negotiationCrumbLabel(n: Negotiation): string {
  return n.label ? `${KIND_LABELS[n.kind]} · ${n.label}` : KIND_LABELS[n.kind];
}

const CARD = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';

interface Props {
  negotiation: Negotiation;
  study: Study;
  onNegotiationUpdated: (negotiation: Negotiation) => void;
  onStudyUpdated: (study: Study) => void;
}

export function NegotiationDetail({ negotiation, study, onNegotiationUpdated, onStudyUpdated }: Props) {
  const { toast } = useToast();
  const [savingField, setSavingField] = useState<string | null>(null);

  async function handleDateChange(key: DateFieldKey, value: string) {
    setSavingField(key);
    const dateValue = value || null;
    // Built per-branch (not a computed-key object literal) so each patch is
    // exactly the single field this control owns — no wider index signature.
    const patch: NegotiationPatch =
      key === 'receivedAt'
        ? { receivedAt: dateValue }
        : key === 'agreedAt'
        ? { agreedAt: dateValue }
        : key === 'feAt'
        ? { feAt: dateValue }
        : { emailReceivedAt: dateValue };
    try {
      const updated = await updateNegotiation(negotiation.id, patch);
      onNegotiationUpdated(updated);
    } catch {
      toast('Could not save that date.', { type: 'error' });
    } finally {
      setSavingField(null);
    }
  }

  async function handleStatusChange(value: string) {
    setSavingField('status');
    try {
      const updated = await updateNegotiation(negotiation.id, { status: value as NegotiationStatus });
      onNegotiationUpdated(updated);
    } catch {
      toast('Could not update status.', { type: 'error' });
    } finally {
      setSavingField(null);
    }
  }

  async function handleRecruitmentChange(value: string) {
    setSavingField('recruitmentDifficulty');
    try {
      const updated = await updateStudy(study.id, { recruitmentDifficulty: value ? Number(value) : null });
      onStudyUpdated(updated);
    } catch {
      toast('Could not update recruitment difficulty.', { type: 'error' });
    } finally {
      setSavingField(null);
    }
  }

  return (
    <div className="space-y-4">
      <section className={CARD}>
        {negotiation.label && <p className="text-sm text-gray-600 dark:text-gray-300">{negotiation.label}</p>}

        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Status {savingField === 'status' && '(saving…)'}
            </span>
            <FilterSelect label="Status" value={negotiation.status} onChange={handleStatusChange} options={STATUS_OPTIONS} />
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Recruitment difficulty {savingField === 'recruitmentDifficulty' && '(saving…)'}
            </span>
            <FilterSelect
              label="Recruitment difficulty"
              value={study.recruitment_difficulty ? String(study.recruitment_difficulty) : ''}
              onChange={handleRecruitmentChange}
              options={RECRUITMENT_OPTIONS}
            />
          </div>
        </div>

        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Turnaround dates
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {DATE_FIELDS.map(({ key, label, pick }) => (
              <div key={key}>
                <label htmlFor={`cta-date-${key}`} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                  {label} {savingField === key && '(saving…)'}
                </label>
                <input
                  id={`cta-date-${key}`}
                  type="date"
                  value={pick(negotiation) ?? ''}
                  onChange={(e) => handleDateChange(key, e.target.value)}
                  className={`${fieldCls} w-full`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${CARD} min-w-0 overflow-x-auto`}>
        <VersionsPanel negotiationId={negotiation.id} />
      </section>

      <section className={CARD}>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Calculator</h4>
        <CalculatorPanel negotiationId={negotiation.id} studyId={study.id} className="mt-2" />
      </section>
    </div>
  );
}

export default NegotiationDetail;
